import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Star, MessageSquare, ShieldCheck } from "lucide-react";
import CoachAvatar from "@/components/CoachAvatar";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareButton from "@/components/ShareButton";
import MessagingDrawer, { directConversationId } from "@/components/MessagingDrawer";
import { DAY_LABELS } from "@/components/CoachAvailabilityCard";
import ReviewCard from "@/components/ReviewCard";

export default function CoachProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [chat, setChat] = useState(false);

  const load = () => {
    api.get(`/coaches/${id}`).then((r) => setCoach(r.data)).catch(() => setNotFound(true));
    api.get("/reviews", { params: { target_type: "coach", target_id: id } }).then((r) => setReviews(r.data));
  };
  useEffect(load, [id]);

  const submitReview = async () => {
    try {
      await api.post("/reviews", { target_type: "coach", target_id: id, rating, comment });
      toast.success("Merci pour votre avis !");
      setComment(""); setRating(5); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Connectez-vous pour laisser un avis"); }
  };

  if (notFound) return <div><Navbar /><div className="max-w-5xl mx-auto p-6 text-slate-500">Coach introuvable. <Link to="/coachs" className="underline">Retour aux coachs</Link></div></div>;
  if (!coach) return <div><Navbar /><div className="max-w-5xl mx-auto p-6 text-slate-500">Chargement…</div></div>;

  const isSelf = user && user.id === coach.id;
  const canContact = user && user.id && !isSelf;

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sc-fade-in" data-testid="coach-profile">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 flex gap-5 items-start">
            <CoachAvatar coach={coach} size="w-28 h-28" textSize="text-3xl" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="coach-name">{coach.name}</h1>
                <VerifiedBadge coach={coach} size="lg" />
              </div>
              <div className="mt-1 text-slate-600 flex items-center gap-1"><MapPin className="w-4 h-4" />{coach.city || "France"}
                {coach.available === false && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">indisponible actuellement</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(coach.specialties || []).map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-700 border border-orange-500/20">{s}</span>)}
              </div>
              <p className="mt-4 text-slate-700">{coach.bio || "Coach adhérent TymCoach."}</p>
              {coach.showcase && (
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-4" data-testid="coach-showcase">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-sky-700">Espace promo · Coach Pro</div>
                  <div className="mt-1 font-bold text-slate-900">{coach.showcase.title}</div>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{coach.showcase.text}</p>
                  {coach.showcase.url && <a data-testid="coach-showcase-link" href={coach.showcase.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex sc-btn sc-btn-outline text-xs">Découvrir</a>}
                </div>
              )}
              {(coach.availability || []).length > 0 && (
                <div className="mt-4" data-testid="coach-availability">
                  <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Disponibilités</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {coach.availability.map((a, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200"><b>{DAY_LABELS[a.day]}</b> {a.start}–{a.end}</span>)}
                  </div>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2 items-center">
                {canContact && (
                  <button data-testid="contact-coach-btn" onClick={() => setChat(true)} className="sc-btn sc-btn-primary"><MessageSquare className="w-4 h-4" /> Contacter {coach.name.split(" ")[0]}</button>
                )}
                {!user || user === false ? <Link data-testid="contact-login-link" to="/connexion" className="sc-btn sc-btn-primary"><MessageSquare className="w-4 h-4" /> Se connecter pour contacter</Link> : null}
                <ShareButton testId="share-coach-profile" path={`/coachs/${coach.id}`} text={`Découvre ${coach.name}, coach ${(coach.specialties || []).slice(0, 2).join(" & ")} sur TymCoach :`} />
              </div>
            </div>
          </div>
          <div className="sc-card">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Note moyenne</div>
            <div className="mt-1 flex items-center gap-2"><Star className="w-5 h-5 text-orange-500" /><span className="text-2xl font-extrabold" data-testid="coach-rating">{coach.rating_avg ?? "—"}</span><span className="text-xs text-slate-500">({coach.reviews_count} avis)</span></div>
            <div className="mt-4 text-xs uppercase font-semibold text-slate-500 tracking-wider">Séances réglées</div>
            <div className="mt-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /><span className="text-2xl font-extrabold" data-testid="coach-paid-sessions">{coach.paid_sessions}</span></div>
            <p className="mt-3 text-xs text-slate-500">{coach.verified ? "Ce coach a validé plusieurs séances payées via TymCoach." : `Badge « vérifié » à partir de 3 séances réglées (${coach.paid_sessions}/3).`}</p>
          </div>
        </div>

        <h2 className="mt-12 text-2xl font-bold text-slate-900">Avis</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} testId={`coach-review-${r.id}`} canReply={!!isSelf} onReplied={load} />
          ))}
          {reviews.length === 0 && <div className="text-slate-500 text-sm">Aucun avis pour le moment.</div>}
        </div>

        {user && user.id && !isSelf && (
          <div className="mt-6 sc-card">
            <div className="font-semibold text-slate-900">Laisser un avis</div>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`coach-review-star-${n}`} onClick={() => setRating(n)} className={`w-8 h-8 rounded-full ${n <= rating ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>{n}</button>
              ))}
            </div>
            <textarea data-testid="coach-review-comment" className="mt-3 w-full border border-slate-200 rounded-xl p-3 text-sm" rows={3} placeholder="Votre expérience avec ce coach…" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button data-testid="coach-review-submit" onClick={submitReview} className="mt-3 sc-btn sc-btn-emerald">Publier l'avis</button>
          </div>
        )}
      </div>

      {chat && user && (
        <MessagingDrawer bookingId={directConversationId(user.id, coach.id)} otherId={coach.id} title={`Conversation avec ${coach.name}`} onClose={() => setChat(false)} />
      )}
      <Footer />
    </div>
  );
}
